import { post_Submit } from "services/online-application-service.openapi.zod";
import { z } from "zod";
import { toSingleLetterFromLowerCaseGender } from "utils/conversions";
import type { ApplicationState } from "store/application/applicationSlice";
import type { DTOCoverageOption } from "types/CoverageOptions";
import {
    validateSubmissionState,
    buildSecondaryAddressee,
    buildApplicantIdentity,
    buildPrimaryBeneficiary,
} from "mappers/shared/submissionMapper.base";

const BodySchema = post_Submit.parameters.shape.body;
type DirectTermSubmissionRequestBody = z.infer<typeof BodySchema>;

/**
 * Transforms DTO Redux state into the format required for submission API
 * @param dtoState - The DTO application state from Redux store
 * @returns Formatted submission request body
 */
export function transformDTOSubmissionData(dtoState: ApplicationState): DirectTermSubmissionRequestBody {
    const {
        quoteData,
        contactData,
        paymentInformationData,
        offerData,
        contactInformation,
        policyBeneficiary,
        secondaryAddressee,
        landingPageData,
    } = validateSubmissionState(dtoState, {
        requireOffer: true,
        requiredContactSections: ["statementOfHealth", "coverageReplacement"],
    });

    // Safe to assert: validateSubmissionState threw if either section was missing
    const validStatementOfHealth = contactData.statementOfHealth!;
    const validCoverageReplacement = contactData.coverageReplacement!;

    // Handle replacement policy
    const replacementPolicy = validCoverageReplacement.replacementIndicator
        ? {
              replacementIndicator: validCoverageReplacement.replacementIndicator,
              replacementCompany: validCoverageReplacement.replacementCompany,
              replacementAccount: validCoverageReplacement.replacementAccount || undefined,
          }
        : {
              replacementIndicator: validCoverageReplacement.replacementIndicator ?? false,
          };

    // Additional data for tracking
    const additionalData = [
        {
            key: "applicantType",
            value: quoteData.applicantType,
        },
    ];

    return {
        applicationID:
            quoteData.applicantType === "member" ? offerData.invitationCode : toSpouseApplicationId(offerData.invitationCode),

        policyNumber: contactData.policyNumber!,
        faceAmount: quoteData.selectedCoverageAmount!,
        modalPremiumAmount: quoteData.selectedCoveragePremium!,

        ...replacementPolicy,

        termPeriod: 20, // Fixed for Direct Term product ?

        applicant: {
            ...buildApplicantIdentity(contactInformation, contactInformation.zipCode), // DTO: zipCode comes from contactInformation
            weight: parseInt(validStatementOfHealth.weight, 10),
            gender: toSingleLetterFromLowerCaseGender(quoteData.gender)!,
            aaaMember: true, // All DTO applications are for AAA members
            keyCode: offerData.keyCode,
            aaaMemberNumber: offerData.aaaMemberNumber,
            height: parseInt(validStatementOfHealth.heightFeet, 10) * 12 + parseInt(validStatementOfHealth.heightInches, 10),
            nicotineUse: quoteData.hasUsedNicotineLastYear!,
            treatment: validStatementOfHealth.hasMedicalConditions ?? false,
            diagnosis: validStatementOfHealth.hasDiagnosticTesting ?? false,
        },

        beneficiaries: [buildPrimaryBeneficiary(policyBeneficiary)],

        ...buildSecondaryAddressee(secondaryAddressee),

        paymentToken: paymentInformationData.paymentToken,
        paymentType: paymentInformationData.paymentType,
        lastFourDigits: paymentInformationData.lastFourDigits,
        acceptanceConfirmed: true, // User must confirm acceptance to reach submission
        hannoverID: contactData.hannoverId!,

        campaign: {
            campaignType: "CAMPAIGNTYPE_DIRECTMAIL",
            productCode: offerData.planCode,
            clubCode: landingPageData?.campaign.clubCode ?? contactData.clubCode!,
            leadSource: landingPageData?.campaign.leadSource,
            campaignName: "DIR",
            campaignCode: landingPageData?.campaign.campaignCode ?? offerData.campaignCode,
            offer:
                quoteData.coverageOptions?.map((option: DTOCoverageOption) => option.coverageAmount) ??
                offerData.offer.map((amt: string) => parseInt(amt, 10)),
        },

        offerPurl: landingPageData?.campaign.offerPurl,

        signedTimestamp: new Date().toISOString(),
        applicationSubmissionTimestamp: new Date().toISOString(),

        additionalData,
    };
}

export function toSpouseApplicationId(memberApplicationId: string | null | undefined) {
    return `${memberApplicationId!.substring(0, 1)}1${memberApplicationId!.substring(2, 10)}`;
}
